import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Organizer } from '@radevents/shared';
import organizerPortalService from '../../services/organizerPortalService';
import { loadAuthedImage, releaseAuthedImage } from '../../utils/authedImage';

/** Die beiden Bilder eines Veranstalterprofils — mehr gibt es nicht. */
export type AssetKind = 'logo' | 'banner';

interface Options {
  organizerId: number;
  /** Nur Verwalter laden Stammdaten und Vorschauen — für Besucher passiert hier nichts. */
  managesThis: boolean;
  /** Nach Upload oder Entfernen: die öffentliche Ansicht neu holen. */
  onPublicChanged: () => void;
  /** Ein 402 heißt „gehört zu Premium" — dann öffnet die Seite den Dialog statt einer Fehlermeldung. */
  onPremiumRequired: () => void;
}

/**
 * Stammdaten, Logo und Banner der eigenen Veranstalterseite.
 *
 * Zusammengezogen aus `OrganizerDetail`: dort lagen Zustand, der Nachlade-Effekt für die
 * wartenden Bilder und die beiden Handler über hundert Zeilen verteilt zwischen Antragsformular
 * und SEO-Daten. Sie gehören zusammen — sie teilen sich `profile`, und jeder von ihnen schreibt
 * es.
 *
 * Der Inhalt ist unverändert übernommen.
 */
export function useOrganizerAssets({ organizerId, managesThis, onPublicChanged, onPremiumRequired }: Options) {
  /** Die vollen Stammdaten — nur für Verwalter, mit Sichtbarkeit und Upload-Zustand. */
  const [profile, setProfile] = useState<Organizer | null>(null);
  const [uploadingAsset, setUploadingAsset] = useState(false);
  /** Gewählte, noch nicht hochgeladene Datei — siehe AssetUploadPreview. */
  const [pickedAsset, setPickedAsset] = useState<{ kind: AssetKind; file: File } | null>(null);
  /** Blob-Adressen der noch nicht freigegebenen Bilder — siehe loadAuthedImage. */
  const [pendingUrls, setPendingUrls] = useState<{ logo: string | null; banner: string | null }>(
    { logo: null, banner: null },
  );

  const loadProfile = useCallback(() => {
    if (!managesThis || !organizerId) { setProfile(null); return; }
    organizerPortalService.profile(organizerId).then(setProfile).catch(() => setProfile(null));
  }, [managesThis, organizerId]);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  /**
   * Holt die Vorschau der wartenden Bilder nach.
   *
   * Zwei Schritte statt einer Bild-URL, weil der Endpunkt die Verknüpfung prüft und ein `<img>`
   * keinen Token mitschickt. Die Blob-Adressen werden beim Wechsel wieder freigegeben.
   */
  useEffect(() => {
    if (!managesThis || !organizerId) { setPendingUrls({ logo: null, banner: null }); return; }
    let alive = true;
    const held: string[] = [];
    const wanted: ('logo' | 'banner')[] = [];
    if (profile?.logoPending) wanted.push('logo');
    if (profile?.bannerPending) wanted.push('banner');
    Promise.all(wanted.map(kind =>
      loadAuthedImage(`/organizer-portal/${organizerId}/${kind}/pending`)
        .then(url => [kind, url] as const)
        .catch(() => [kind, null] as const),
    )).then(pairs => {
      if (!alive) { pairs.forEach(([, url]) => releaseAuthedImage(url)); return; }
      // Freigeben außerhalb des State-Updaters: der läuft im Entwicklungsmodus zweimal, und
      // Nebenwirkungen darin sind genau deshalb keine gute Idee.
      pairs.forEach(([, url]) => { if (url) held.push(url); });
      setPendingUrls(prev => {
        releaseAuthedImage(prev.logo);
        releaseAuthedImage(prev.banner);
        const next = { logo: null as string | null, banner: null as string | null };
        pairs.forEach(([kind, url]) => { next[kind] = url; });
        return next;
      });
    });
    // Beim Verlassen der Seite gibt sonst niemand die Bytes frei — ein Banner sind bis zu 2 MB,
    // die der Browser bis zum nächsten vollständigen Seitenwechsel festhält.
    return () => { alive = false; held.forEach(releaseAuthedImage); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [managesThis, organizerId, profile?.logoPending, profile?.bannerPending]);

  const uploadAsset = async (kind: AssetKind, file: File) => {
    setUploadingAsset(true);
    try {
      const updated = await organizerPortalService.uploadAsset(organizerId, kind, file);
      setProfile(updated);
      onPublicChanged();
      // Wahrheitsgemäß: das Bild ist hochgeladen, aber noch nicht öffentlich. „Gespeichert"
      // allein ließe den Veranstalter glauben, es stünde bereits auf seiner Seite.
      toast.success(kind === 'logo'
        ? 'Logo hochgeladen — es erscheint nach kurzer Prüfung'
        : 'Banner hochgeladen — es erscheint nach kurzer Prüfung');
      setPickedAsset(null);
    } catch (err: any) {
      // 402 heißt: gehört zu Premium. Dann kein Fehler, sondern das Angebot — der Server ist die
      // Grenze, das Fenster ist die Erklärung. Kommt seit der Premium-Runde nur noch beim
      // Banner; das Logo ist für alle frei.
      if (err?.response?.status === 402) onPremiumRequired();
      else toast.error(err?.response?.data?.error || 'Datei konnte nicht gespeichert werden');
    } finally {
      setUploadingAsset(false);
    }
  };

  const removeAsset = async (kind: AssetKind) => {
    if (!window.confirm(kind === 'logo' ? 'Logo wirklich entfernen?' : 'Banner wirklich entfernen?')) return;
    setUploadingAsset(true);
    try {
      const updated = await organizerPortalService.deleteAsset(organizerId, kind);
      setProfile(updated);
      onPublicChanged();
    } catch {
      toast.error('Konnte nicht entfernt werden');
    } finally {
      setUploadingAsset(false);
    }
  };

  return {
    profile, setProfile, loadProfile,
    uploadingAsset, pickedAsset, setPickedAsset, pendingUrls,
    uploadAsset, removeAsset,
  };
}
